/* Dense terminal grid: sticky accent header strip, zebra rows, pointer
   highlight, sizing handles, drag targets and per-column filter dropdowns. */

.board {
  display: grid;
  min-width: 0;
  gap: 8px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.matchCount {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  color: var(--ink-muted);
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
}

.clearFilters {
  padding: 0;
  border: 0;
  background: none;
  color: var(--signal);
  cursor: pointer;
  font: inherit;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.export {
  border-radius: 6px;
  min-height: 32px;
  padding: 0 14px;
  border: 1px solid var(--line-strong);
  background: #0d0d0d;
  color: var(--ink);
  cursor: pointer;
  font: inherit;
  font-size: 0.74rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.export:hover,
.export:focus-visible {
  border-color: var(--signal);
  background: var(--signal-fill);
  color: var(--on-signal);
}

.scroll,
.scrollMenuOpen {
  min-width: 0;
  width: 100%;
  overflow: auto;
  overscroll-behavior-x: contain;
  /* The grid scrolls in its own frame so the header row stays pinned. */
  max-height: calc(100vh - 13rem);
  border: 1px solid var(--line);
  background: var(--paper-raised);
  border-radius: 8px;
}

/* Room for an open dropdown even when filters leave few rows. */
.scrollMenuOpen {
  min-height: 21rem;
}

.table {
  font-family: var(--mono);
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;
  table-layout: fixed;
}

.table th,
.table td {
  text-align: right;
}

.table td {
  padding: 3px 8px;
  overflow: hidden;
  font-size: 0.78rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-bottom: 1px solid #161613;
}

.table tbody tr:nth-child(even) td {
  background: #050505;
}

.table tbody tr:hover td {
  background: #170d2a;
}

.table thead th {
  position: sticky;
  top: 0;
  z-index: 5;
  padding: 0;
  background: var(--head-fill);
  color: var(--signal);
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-bottom: 1px solid var(--line-strong);
}

.table th:first-child,
.table td:first-child,
.table td[data-kind="text"] {
  text-align: left;
}

.headerControls {
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr) 26px;
  gap: 2px;
  align-items: center;
  min-height: 34px;
  padding-right: 4px;
}

.grip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-muted);
  cursor: grab;
  font-size: 0.7rem;
  opacity: 0.5;
  user-select: none;
}

.grip:hover {
  opacity: 1;
}

.headerButton {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  width: 100%;
  min-width: 0;
  min-height: 26px;
  padding: 4px 2px;
  border: 0;
  background: none;
  color: inherit;
  cursor: pointer;
  font: inherit;
  letter-spacing: inherit;
  text-align: left;
  text-transform: inherit;
  white-space: nowrap;
}

.headerButtonSorted {
  composes: headerButton;
  color: var(--ink);
}

.headerButton:hover,
.headerButtonSorted:hover,
.filterButton:hover {
  color: var(--ink);
}

.sortMark {
  flex: 0 0 auto;
  opacity: 0.6;
  font-size: 0.85em;
}

.headerButtonSorted .sortMark {
  opacity: 1;
}

.filterButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  min-height: 26px;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  cursor: pointer;
}

.chevron {
  opacity: 0.6;
}

.chevronActive {
  color: var(--ink);
}

.resizer {
  position: absolute;
  top: 0;
  right: 0;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  touch-action: none;
  user-select: none;
}

.resizer:hover {
  background: var(--signal-soft);
}

.menu {
  position: absolute;
  top: calc(100% + 2px);
  right: 0;
  z-index: 30;
  display: grid;
  min-width: 11rem;
  padding: 4px;
  border: 1px solid var(--line-strong);
  background: #0d0d0d;
  text-transform: none;
  letter-spacing: normal;
}

.table th:first-child .menu {
  right: auto;
  left: 0;
}

.option,
.optionActive {
  min-height: 32px;
  padding: 0 10px;
  border: 0;
  background: none;
  color: var(--ink);
  cursor: pointer;
  font: inherit;
  font-size: 0.78rem;
  text-align: left;
}

.option:hover {
  background: var(--signal-soft);
}

.optionActive {
  background: var(--signal-fill);
  color: var(--on-signal);
  font-weight: 700;
}

.menuInput {
  width: 100%;
  min-width: 10rem;
  min-height: 32px;
  padding: 4px 8px;
  border: 1px solid var(--line-strong);
  background: #000;
  color: var(--ink);
  font: inherit;
  font-size: 0.8rem;
}

.token {
  color: var(--signal);
  font-weight: 700;
  text-decoration: none;
}

a.token:hover {
  text-decoration: underline;
  text-underline-offset: 3px;
}

/* The ticker as a key that opens the token panel. */
.tokenButton {
  composes: token;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
  text-align: left;
}

.tokenButton:hover {
  text-decoration: underline;
  text-underline-offset: 3px;
}

/* Secondary explorer link beside the ticker. */
.explorer {
  margin-left: 5px;
  color: var(--ink-muted);
  font-size: 0.85em;
  text-decoration: none;
}

.explorer:hover {
  color: var(--signal);
}

.name {
  margin-left: 8px;
  color: var(--ink-muted);
  font-size: 0.9em;
}

.halted {
  margin-left: 8px;
  padding: 0 4px;
  background: var(--down);
  color: #000;
  font-size: 0.75em;
  text-transform: uppercase;
}

.absent {
  color: var(--ink-muted);
}

.up {
  color: var(--up);
}

.down {
  color: var(--down);
}

/* A price that is not the venue's own quote (price_note): the reason on hover or focus. */
.priceNote {
  margin-left: 4px;
  color: var(--caution);
  cursor: help;
  font-size: 0.72rem;
}

.priceNote:focus-visible {
  outline: 1px solid var(--signal);
  outline-offset: 1px;
}

/* The net flow column's window (1d / 7d / 30d), C-6c. */
.flowWindow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--ink-muted);
  font-size: 0.72rem;
}

.flowWindow select {
  min-height: 26px;
  padding: 0 6px;
  border: 1px solid var(--line-strong);
  background: #0d0d0d;
  color: var(--ink);
  font: inherit;
  font-size: 0.72rem;
}

/* A missing figure's short reason ("not counted", "no price"), C-6c cleanup. */
.missing {
  color: var(--ink-muted);
  font-size: 0.92em;
}
